import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WidgetNewsfeed } from "widgets/src/WidgetNewsfeed";
import { WAIntegrationNewsfeedQuery } from "./__generated__/WAIntegrationNewsfeedQuery.graphql";
import { WAIntegrationNewsfeedMutation } from "./__generated__/WAIntegrationNewsfeedMutation.graphql";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { newsfeedWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/newsfeedWidgetSizes";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddIntegrationNewsfeed">;

export const WAIntegrationNewsfeed = withWebModal(WAIntegrationNewsfeedC, true);
function WAIntegrationNewsfeedC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWAIntegrationNewsfeed {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAIntegrationNewsfeed(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAIntegrationNewsfeedQuery>(
    graphql`
      query WAIntegrationNewsfeedQuery(
        $storecastId: ID!
        $input: PreviewWidgetNewsfeedInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetNewsfeed(input: $input) {
            ...WidgetNewsfeed_widgetNewsfeed
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      input: {
        size: "TWO_X_FOUR",
        newsfeedId: props.route.params.newsfeedId,
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidget, loading] = useMutation<WAIntegrationNewsfeedMutation>(
    graphql`
      mutation WAIntegrationNewsfeedMutation(
        $storecastId: ID!
        $input: CreateWidgetNewsfeedInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetNewsfeed(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidget({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
            newsfeedId: props.route.params.newsfeedId,
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidget,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
      props.route.params.newsfeedId,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={newsfeedWidgetSizes}
        defaultSize={{ w: 2, h: 2 }}
      >
        {data.dashboard.previewWidgetNewsfeed ? (
          <WidgetNewsfeed
            widget={data.dashboard.previewWidgetNewsfeed}
            storecastId={props.route.params.storecastId}
            interactive={false}
            mode="IDLE"
          />
        ) : null}
      </WASizePreview>
    </View>
  );
}
